/**
 * Free Gift — AlignerCo Canada (Dawn 15 based theme "Alignerco Canada - New Theme")
 *
 * Two ways to reach checkout on alignerco.ca, both covered:
 *   A) SmileAdvantage / NightAdvantage -> Add to Cart -> /pages/recommendation/?id=VARIANT
 *      -> "Continue To Checkout" builds a cart permalink (/cart/VARIANT:QTY,ADDON:1,...) and goes
 *      straight to checkout. We append the gift to that link: /cart/...,GIFT:QTY.
 *      (Needs a one-line hook in assets/script.js — see the guide.)
 *   B) SmileFlex Easy / NightFlex Easy (and the header cart icon) -> /cart page -> Checkout.
 *      On the cart page we keep the gift line in sync, like the US store.
 *
 * Rules: gift quantity = total quantity of qualifying products; gift price shows "Free";
 * gift +/- locked (remove still works and is respected); never more gifts than stock;
 * sold out -> no gift, no error; checkout waits for a running gift update.
 * Settings come from snippets/free-gift-ca.liquid (window.FreeGiftCAConfig).
 * It only OBSERVES the theme's cart requests and never modifies them.
 */
(function () {
  const cfg = window.FreeGiftCAConfig;
  if (!cfg || !cfg.enabled || window.__freeGiftCALoaded) return;
  window.__freeGiftCALoaded = true;

  const GIFT_VARIANT_ID = String(cfg.giftVariantId);
  const QUALIFYING_VARIANT_IDS = new Set((cfg.qualifyingVariantIds || []).map(String));
  const QUALIFYING_PRODUCT_IDS = new Set((cfg.qualifyingProductIds || []).map(String));
  const GIFT_STOCK = typeof cfg.giftStock === 'number' ? cfg.giftStock : null; // null = stock not tracked
  if (!GIFT_VARIANT_ID || (!QUALIFYING_VARIANT_IDS.size && !QUALIFYING_PRODUCT_IDS.size)) return;

  /* =========================================================================================
   * A) Recommendation page: add the gift to the checkout permalink
   * ========================================================================================= */

  // Called by the one-line hook in assets/script.js (".btnCheckout" click handler).
  // Example: https://alignerco.ca/cart/44501918810432:1,48574612209984:1
  //       -> https://alignerco.ca/cart/44501918810432:1,48574612209984:1,67754361520448:1
  function addGiftToPermalink(url) {
    try {
      if (cfg.giftAvailable === false) return url; // sold out: checkout without gift, no error
      const match = String(url).match(/\/cart\/([^?#]+)/);
      if (!match) return url;
      const lines = match[1].split(',').map((part) => part.split(':'));
      if (lines.some(([id]) => id === GIFT_VARIANT_ID)) return url; // already there
      const qualifying = lines
        .filter(([id]) => QUALIFYING_VARIANT_IDS.has(id))
        .reduce((sum, [, qty]) => sum + (parseInt(qty, 10) || 1), 0);
      if (!qualifying) return url;
      const giftQty = GIFT_STOCK === null ? qualifying : Math.min(qualifying, GIFT_STOCK);
      if (giftQty < 1) return url;
      return String(url).replace(match[1], `${match[1]},${GIFT_VARIANT_ID}:${giftQty}`);
    } catch (_) {
      return url; // never block checkout
    }
  }

  window.FreeGiftCA = { addGiftToPermalink };

  /* =========================================================================================
   * B) Cart page (/cart): keep the gift line in sync
   * ========================================================================================= */

  if (!cfg.isCartPage) return;

  const DEBOUNCE_MS = 150;
  const MAX_FIXES = 15; // loop safety: at most 15 gift changes per 30 s
  const FIX_WINDOW_MS = 30000;
  const MAX_BUSY_WAITS = 40;
  const MAX_STOCK_STEPS = 5;
  const DECLINED_KEY = 'free-gift-declined-ca';
  const CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const FREE_LABEL = cfg.freeLabel || 'Free';
  const ZERO_PRICE = /^(?=.*[^\d\s.,])[^\d]*0(?:[.,]00)?[^\d]*$/; // "$0.00", "$0", "0,00 $"

  const routes = window.routes || {};
  const withJs = (url) => (url.endsWith('.js') ? url : `${url}.js`);
  const CART_URL = withJs(routes.cart_url || '/cart');
  const ADD_URL = withJs(routes.cart_add_url || '/cart/add');
  const UPDATE_URL = withJs(routes.cart_update_url || '/cart/update');

  const nativeFetch = window.fetch.bind(window); // our own requests are never observed (no loops)

  let timer = null;
  let running = false;
  let pending = false;
  let busyWaits = 0;
  let inflight = 0;
  let latestCart = null;
  let fixLog = [];
  let giftCap = GIFT_STOCK === null ? Infinity : GIFT_STOCK;
  let giftSoldOut = cfg.giftAvailable === false;
  let giftRemovalRequested = false;
  let cartCleared = false;

  /* ---------- watch the theme's cart requests (read-only) ---------- */

  window.fetch = function (input, init) {
    let url = '';
    try {
      url = input instanceof Request ? input.url : String(input);
      if (CART_MUTATION_URL.test(url)) noteGiftRemoval(url, init && init.body);
    } catch (_) {
      /* never interfere */
    }
    const request = nativeFetch(input, init);
    try {
      if (CART_MUTATION_URL.test(url)) {
        inflight++;
        request.then(
          (response) => {
            let copy = null;
            try {
              copy = response.clone();
            } catch (_) {
              /* body already used */
            }
            const done = (data) => {
              inflight = Math.max(0, inflight - 1);
              rememberCart(data);
              schedule();
            };
            if (copy) copy.json().then(done, () => done(null));
            else done(null);
          },
          () => {
            inflight = Math.max(0, inflight - 1);
            schedule();
          }
        );
      }
    } catch (_) {
      /* never interfere */
    }
    return request;
  };

  function rememberCart(data) {
    const isFullCart = data && Array.isArray(data.items) && typeof data.item_count === 'number' && data.token;
    latestCart = isFullCart ? data : null;
  }

  // "No thanks" is recorded only when the customer removes the gift line itself.
  function readBody(body) {
    if (!body) return null;
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (_) {
        return Object.fromEntries(new URLSearchParams(body));
      }
    }
    if (body instanceof FormData || body instanceof URLSearchParams) return Object.fromEntries(body.entries());
    return null;
  }

  function isGiftRef(ref) {
    const value = String(ref ?? '');
    return value === GIFT_VARIANT_ID || value.startsWith(`${GIFT_VARIANT_ID}:`);
  }

  function variantAtLine(line) {
    // Dawn cart rows: <tr id="CartItem-N"> ... <input data-quantity-variant-id="...">
    return document.querySelector(`#CartItem-${line} [data-quantity-variant-id]`)?.dataset.quantityVariantId;
  }

  function noteGiftRemoval(url, body) {
    if (/\/cart\/clear/.test(url)) cartCleared = true;
    const data = readBody(body);
    if (!data) return;
    if (/\/cart\/change/.test(url) && Number(data.quantity) === 0) {
      if (isGiftRef(data.id) || (data.line && isGiftRef(variantAtLine(Number(data.line))))) giftRemovalRequested = true;
    }
    if (/\/cart\/update/.test(url)) {
      const updates = data.updates && typeof data.updates === 'object' && !Array.isArray(data.updates) ? data.updates : {};
      for (const [key, value] of Object.entries(data)) {
        const m = /^updates\[(.+)\]$/.exec(key);
        if (m) updates[m[1]] = value;
      }
      for (const [key, value] of Object.entries(updates)) {
        if (isGiftRef(key) && Number(value) === 0) giftRemovalRequested = true;
      }
    }
  }

  /* ---------- gift row: lock quantity, show "Free" ---------- */

  const giftRows = () =>
    [...document.querySelectorAll(`[data-quantity-variant-id="${GIFT_VARIANT_ID}"]`)]
      .map((input) => input.closest('.cart-item'))
      .filter(Boolean);

  function showFreeLabel(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const text = node.nodeValue.trim();
      if (text && text !== FREE_LABEL && ZERO_PRICE.test(text)) node.nodeValue = FREE_LABEL;
    }
  }

  function decorateGiftRows() {
    for (const row of giftRows()) {
      for (const button of row.querySelectorAll('.quantity__button')) {
        if (!button.disabled) button.disabled = true;
      }
      // read-only (NOT disabled): the cart form submits updates[] for every line on checkout
      const input = row.querySelector('.quantity__input');
      if (input && !input.readOnly) input.readOnly = true;
      showFreeLabel(row);
    }
  }

  let decorateQueued = false;
  new MutationObserver(() => {
    if (decorateQueued) return;
    decorateQueued = true;
    requestAnimationFrame(() => {
      decorateQueued = false;
      decorateGiftRows();
    });
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['disabled'],
  });
  decorateGiftRows();

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('.quantity__button') : null;
      if (button && giftRows().some((row) => row.contains(button))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  /* ---------- checkout waits for a running gift update ---------- */

  let resumingCheckout = false;
  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target instanceof Element ? event.target.closest('button[name="checkout"]') : null;
      if (!button || resumingCheckout || isIdle()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      await waitForIdle(4000);
      const target = button.isConnected ? button : document.querySelector('button[name="checkout"]');
      resumingCheckout = true;
      try {
        target?.click();
      } finally {
        resumingCheckout = false;
      }
    },
    true
  );

  function isIdle() {
    return timer === null && !running && !pending && inflight === 0;
  }

  async function waitForIdle(maxMs) {
    const started = Date.now();
    while (!isIdle() && Date.now() - started < maxMs) await new Promise((r) => setTimeout(r, 100));
  }

  /* ---------- sync ---------- */

  if (cfg.syncOnLoad) schedule();
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedule();
  });

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(run, DEBOUNCE_MS);
  }

  async function run() {
    timer = null;
    if (running) {
      pending = true;
      return;
    }
    // Wait while the theme (Dawn) is updating the cart.
    if ((inflight > 0 || document.querySelector('.cart__items--disabled')) && busyWaits < MAX_BUSY_WAITS) {
      busyWaits++;
      schedule();
      return;
    }
    busyWaits = 0;
    running = true;
    try {
      await sync();
    } catch (error) {
      console.warn('[free-gift-ca] sync skipped:', error);
    } finally {
      running = false;
      if (pending) {
        pending = false;
        schedule();
      }
    }
  }

  async function sync() {
    const cart = latestCart || (await getCart());
    latestCart = null;
    const removalRequested = giftRemovalRequested;
    const cleared = cartCleared;
    giftRemovalRequested = false;
    cartCleared = false;
    if (cleared && isDeclined(cart.token)) setDeclined(cart.token, false);

    const items = cart.items || [];
    const target = items
      .filter((item) => QUALIFYING_PRODUCT_IDS.has(String(item.product_id)))
      .reduce((sum, item) => sum + item.quantity, 0);
    const giftLines = items.filter((item) => String(item.variant_id) === GIFT_VARIANT_ID);
    const giftQty = giftLines.reduce((sum, item) => sum + item.quantity, 0);

    if (target === 0) {
      if (isDeclined(cart.token)) setDeclined(cart.token, false);
      if (giftQty > 0 && allowFix()) await withCartLocked(() => setGiftLines(giftLines, 0));
      return;
    }

    const wanted = Math.min(target, giftCap);

    if (giftQty > 0) {
      if (isDeclined(cart.token)) setDeclined(cart.token, false);
      if ((giftQty !== wanted || giftLines.length > 1) && allowFix()) {
        await withCartLocked(async () => {
          if (wanted > giftQty) {
            // "update" does not check stock, "add" does: merge, then add only the extra gifts
            if (giftLines.length > 1) await setGiftLines(giftLines, giftQty);
            await addGifts(giftQty, wanted - giftQty);
          } else {
            await setGiftLines(giftLines, wanted);
          }
        });
      }
      return;
    }

    if (removalRequested) setDeclined(cart.token, true);
    if (!isDeclined(cart.token) && !giftSoldOut && wanted > 0 && allowFix()) {
      await withCartLocked(() => addGifts(0, wanted));
    }
  }

  function isDeclined(token) {
    try {
      return !!token && localStorage.getItem(DECLINED_KEY) === String(token);
    } catch (_) {
      return false;
    }
  }

  function setDeclined(token, value) {
    try {
      if (value && token) localStorage.setItem(DECLINED_KEY, String(token));
      else localStorage.removeItem(DECLINED_KEY);
    } catch (_) {
      /* storage unavailable */
    }
  }

  // Add `extra` gifts (cart already has `current`); step down if Shopify refuses for stock.
  async function addGifts(current, extra) {
    for (let qty = extra, tries = 0; qty > 0 && tries < MAX_STOCK_STEPS; qty--, tries++) {
      const response = await nativeFetch(
        ADD_URL,
        postJSON({
          items: [{ id: Number(GIFT_VARIANT_ID), quantity: qty, properties: { _free_gift: 'true' } }],
          ...sectionsPayload(),
        })
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && !data.status) {
        if (qty < extra) giftCap = current + qty;
        refreshCart(data.sections);
        return true;
      }
      if (response.status !== 422 && data.status !== 422) return false;
    }
    if (current === 0) giftSoldOut = true;
    else giftCap = current;
    return false;
  }

  async function setGiftLines(giftLines, quantity) {
    const updates = {};
    giftLines.forEach((line, index) => {
      updates[line.key] = index === 0 ? Math.max(quantity, 0) : 0;
    });
    const response = await nativeFetch(UPDATE_URL, postJSON({ updates, ...sectionsPayload() }));
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) return false;
    refreshCart(data.sections);
    return true;
  }

  async function getCart() {
    const response = await nativeFetch(CART_URL, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  function postJSON(body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    };
  }

  // Cart page sections to re-render (cart items, cart footer/totals) + the header cart bubble.
  function cartSectionIds() {
    const ids = [...document.querySelectorAll('.shopify-section')]
      .filter((s) => s.querySelector('cart-items, #main-cart-items, #main-cart-footer, .cart__footer'))
      .map((s) => s.id.replace(/^shopify-section-/, ''));
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    return [...new Set(ids)];
  }

  function sectionsPayload() {
    const ids = cartSectionIds();
    return ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {};
  }

  function refreshCart(sectionsHtml) {
    try {
      if (!sectionsHtml) throw new Error('no sections');
      for (const id of cartSectionIds()) {
        const html = sectionsHtml[id];
        if (!html) continue;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-icon-bubble') {
          const bubble = document.getElementById('cart-icon-bubble');
          const fresh = doc.querySelector('.shopify-section');
          if (bubble && fresh) bubble.innerHTML = fresh.innerHTML;
          continue;
        }
        const current = document.getElementById(`shopify-section-${id}`);
        const fresh = doc.getElementById(`shopify-section-${id}`);
        if (current && fresh) current.innerHTML = fresh.innerHTML;
      }
    } catch (_) {
      window.location.reload(); // last resort: show the correct cart
    }
  }

  async function withCartLocked(task) {
    const list = document.getElementById('main-cart-items');
    list?.classList.add('cart__items--disabled');
    try {
      return await task();
    } finally {
      list?.classList.remove('cart__items--disabled');
    }
  }

  function allowFix() {
    const now = Date.now();
    fixLog = fixLog.filter((t) => now - t < FIX_WINDOW_MS);
    if (fixLog.length >= MAX_FIXES) {
      console.warn('[free-gift-ca] paused: too many gift changes in a short time');
      return false;
    }
    fixLog.push(now);
    return true;
  }
})();